import { useEffect } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { Moon, Sun, Search, Plus, Car, Map as MapIcon, Settings, LogOut, Wifi, WifiOff, Fingerprint, SlidersHorizontal, BellRing } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { usePrefs } from "@/context/PrefsContext";
import { useNow } from "@/hooks/useNow";
import { fmtTime, fmtDate } from "@/lib/format";

const Btn = ({ testId, onClick, icon: Icon, children, kbd, primary }) => (
  <button data-testid={testId} onClick={onClick} className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-xs font-medium transition-colors ${primary ? "bg-primary text-primary-foreground font-semibold hover:bg-primary/90" : "border bg-card hover:bg-accent"}`}>
    <Icon className="h-3.5 w-3.5" /> {children} {kbd && <kbd className={`mono text-[10px] ml-1 ${primary ? "opacity-80" : "text-muted-foreground"}`}>{kbd}</kbd>}
  </button>
);

export function TopBar({ onNewCall, onOfficerInitiated, onSearch, onMap, onEntitySearch, onPreferences, realtime }) {
  const { user, logout, isAdmin } = useAuth();
  const { theme, setTheme, settings, hotkeys } = usePrefs();
  const now = useNow(1000);
  const nowIso = new Date(now).toISOString();
  const alertsQ = useQuery({ queryKey: ["alerts-summary"], queryFn: async () => (await api.get("/alerts/summary")).data, enabled: isAdmin, refetchInterval: 120000 });
  const needs = (alertsQ.data?.due || 0) + (alertsQ.data?.lapsed || 0);

  useEffect(() => {
    if (isAdmin && needs > 0 && !sessionStorage.getItem("cad_alert_reminder")) {
      sessionStorage.setItem("cad_alert_reminder", "1");
      toast.warning(`${alertsQ.data.lapsed} location alert(s) lapsed · ${alertsQ.data.due} due for review`, { description: "Admin → Location alerts", duration: 8000 });
    }
  }, [isAdmin, needs, alertsQ.data]);

  return (
    <header className="flex items-center gap-3 px-3 py-1.5 border-b bg-card/80 backdrop-blur-md shrink-0" data-testid="top-bar">
      <div className="flex items-center gap-2 mr-2">
        <span className="h-6 w-6 rounded-md bg-primary/15 border border-primary/40 text-primary flex items-center justify-center mono text-[10px] font-semibold">CAD</span>
        <div className="leading-tight">
          <div className="display text-sm font-semibold">CAD-Lite</div>
          <div className="overline-label truncate max-w-[220px]" data-testid="agency-name">{settings.name || "Agency"}</div>
        </div>
      </div>
      <div className="flex items-center gap-1.5">
        <Btn testId="new-call-button" onClick={onNewCall} icon={Plus} kbd={hotkeys.new_call} primary>New Call</Btn>
        <Btn testId="officer-initiated-button" onClick={onOfficerInitiated} icon={Car} kbd={hotkeys.officer_initiated}>Officer-Initiated</Btn>
        <Btn testId="search-button" onClick={onSearch} icon={Search} kbd={hotkeys.search}>Search</Btn>
        <Btn testId="entity-search-button" onClick={onEntitySearch} icon={Fingerprint} kbd={hotkeys.entity_search}>Entity</Btn>
        <Btn testId="map-button" onClick={onMap} icon={MapIcon} kbd={hotkeys.map}>Map</Btn>
      </div>
      <div className="ml-auto flex items-center gap-3">
        <div className="text-right leading-tight hidden md:block">
          <div className="mono text-base font-medium tabular-nums" data-testid="clock-time">{fmtTime(nowIso)}</div>
          <div className="overline-label">{fmtDate(nowIso)} · {settings.timezone || "local"}</div>
        </div>
        <span data-testid="realtime-status" title={`Realtime: ${realtime}`} className={`chip ${realtime === "connected" ? "border-emerald-500/50 text-emerald-600 dark:text-emerald-300" : "border-amber-500/50 text-amber-600 dark:text-amber-300"}`}>
          {realtime === "connected" ? <Wifi className="h-3 w-3" /> : <WifiOff className="h-3 w-3" />} {realtime === "connected" ? "LIVE" : "RECONNECTING"}
        </span>
        <button data-testid="theme-toggle" onClick={() => setTheme(theme === "dark" ? "light" : "dark")} className="h-8 w-8 rounded-md border bg-card hover:bg-accent flex items-center justify-center transition-colors" aria-label="Toggle theme">
          {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
        </button>
        <button data-testid="preferences-button" onClick={onPreferences} className="h-8 w-8 rounded-md border bg-card hover:bg-accent flex items-center justify-center transition-colors" aria-label="My preferences" title="Theme & hotkeys">
          <SlidersHorizontal className="h-4 w-4" />
        </button>
        {isAdmin && (
          <Link data-testid="admin-link" to={needs > 0 ? "/admin/alerts" : "/admin"} className="relative h-8 px-2.5 rounded-md border bg-card hover:bg-accent flex items-center gap-1.5 text-xs font-medium transition-colors">
            <Settings className="h-3.5 w-3.5" /> Admin
            {needs > 0 && <span data-testid="admin-alert-badge" title="Location alerts due for review or lapsed" className="chip mono !py-0 border-amber-500/60 text-amber-700 dark:text-amber-300 bg-amber-500/10"><BellRing className="h-3 w-3" /> {needs}</span>}
          </Link>
        )}
        <div className="flex items-center gap-2 pl-3 border-l">
          <div className="leading-tight text-right">
            <div className="text-xs font-medium" data-testid="user-display-name">{user?.display_name}</div>
            <div className="overline-label">{user?.role}</div>
          </div>
          <button data-testid="logout-button" onClick={logout} className="h-8 w-8 rounded-md border bg-card hover:bg-accent flex items-center justify-center transition-colors" aria-label="Sign out">
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </div>
    </header>
  );
}
